// src/pages/AccountActivation.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { CheckCircle, XCircle, Loader, ArrowRight, ArrowLeft, Mail, Send } from 'lucide-react';
import api from '../utils/apiClient';

// Animations
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Styled Components
const ParticlesWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const ActivationContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
`;

const ContentCard = styled(motion.div)`
  max-width: 500px;
  width: 100%;
  padding: ${({ theme }) => theme.spacing[8]};
  background: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  backdrop-filter: blur(20px);
  box-shadow: ${({ theme }) => theme.shadows.large};
  text-align: center;
`;

const StatusIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing[6]};
  font-size: 2.5rem;
  
  ${props => props.status === 'loading' && css`
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, #0891b2 100%);
    color: white;
    animation: ${pulse} 2s infinite;
    
    svg {
      animation: ${spin} 1s linear infinite;
    }
  `}
  
  ${props => props.status === 'success' && css`
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.success} 0%, #059669 100%);
    color: white;
    animation: ${pulse} 0.6s ease-out;
  `}
  
  ${props => props.status === 'error' && css`
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.error} 0%, #dc2626 100%);
    color: white;
  `}
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.primary};
`;

const Message = styled.p`
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  line-height: 1.6;
`;

const ActionButton = styled(motion.button)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
  }
`;

const ResendForm = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: rgba(0, 210, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid rgba(0, 210, 255, 0.1);
`;

const ResendFormTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.glow};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;


const AccountActivation = () => {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams(); // Get all parameters
  const { uidb64, token } = params; // Extract specific parameters
  
  // Initialize particles
  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = {
    background: {
      color: {
        value: "transparent",
      },
    },
    fpsLimit: 60,
    interactivity: {
      events: {
        onHover: {
          enable: true,
          mode: "repulse",
        },
        resize: true,
      },
      modes: {
        repulse: {
          distance: 100,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: "#00d2ff",
      },
      links: {
        color: "#00d2ff",
        distance: 150,
        enable: true,
        opacity: 0.2,
        width: 1,
      },
      collisions: {
        enable: false,
      },
      move: {
        directions: "none",
        enable: true,
        outModes: {
          default: "bounce",
        },
        random: false,
        speed: 1,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 800,
        },
        value: 50,
      },
      opacity: {
        value: 0.3,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 3 },
      },
    },
    detectRetina: true,
  };
  
  useEffect(() => {
    const activateAccount = async () => {
      // Use path parameters instead of query parameters
      const uid = uidb64;
      const activationToken = token;
      
      if (!uid || !activationToken) {
        setStatus('error');
        setMessage('Invalid activation link. Please check your email and try again.');
        return;
      }
      
      try {
        const result = await api.auth.activate(uid, activationToken);
        setStatus('success');
        setMessage('Your account has been successfully activated! You can now sign in.');
      } catch (error) {
        // Check if the error message indicates that the account is already activated
        const errorData = error.response?.data;
        const errorMessage = errorData?.detail ||
          errorData?.message ||
          errorData?.error ||
          (errorData?.non_field_errors && errorData.non_field_errors[0]) ||
          JSON.stringify(errorData) ||
          error.message ||
          'Failed to activate your account. The link may have expired.';

        // If the error message indicates that the account is already activated, 
        // treat this as a success case rather than an error
        if (errorMessage.toLowerCase().includes('already activated') ||
          errorMessage.toLowerCase().includes('already active') ||
          errorMessage.toLowerCase().includes('activation has already been confirmed')) {
          setStatus('success');
          setMessage('Your account is already activated! You can sign in.');
        } else {
          setStatus('error');
          setMessage(errorMessage);
        }
      }
    };
    
    activateAccount();
  }, [uidb64, token]); // Update dependencies
  
  const handleResendActivation = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    
    setResending(true);
    try {
      await api.auth.resendActivation(resendEmail);
      setMessage('Activation email sent successfully! Please check your inbox.');
      setShowResendForm(false);
      setResendEmail('');
    } catch (error) {
      // Show exact error message from backend
      let errorMessage = 'Failed to send activation email. Please try again.';

      if (error.response?.data) {
        errorMessage = error.response.data.detail ||
          error.response.data.message ||
          error.response.data.error ||
          error.response.data.non_field_errors?.[0] ||
          JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage(errorMessage);
    } finally {
      setResending(false);
    }
  };
  
  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader />;
      case 'success':
        return <CheckCircle />;
      case 'error':
        return <XCircle />;
      default:
        return null;
    }
  };
  
  const getTitle = () => {
    switch (status) {
      case 'loading':
        return 'Activating Your Account...';
      case 'success':
        return 'Account Activated!';
      case 'error':
        return 'Activation Failed';
      default:
        return '';
    }
  };
  
  return (
    <>
      <ParticlesWrapper>
        <Particles
          id="account-activation-particles"
          init={particlesInit}
          options={particlesOptions}
        />
      </ParticlesWrapper>
      
      <ActivationContainer>
        <ContentCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <StatusIcon status={status}>
            {getStatusIcon()}
          </StatusIcon>
          
          <Title>{getTitle()}</Title>
          <Message>{message}</Message>
          
          {status === 'success' && (
            <ActionButton
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/auth?tab=login')}
            >
              <ArrowRight size={20} />
              Go to Sign In
            </ActionButton>
          )}
          
          {status === 'error' && !showResendForm && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <ActionButton
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowResendForm(true)}
              >
                <Mail size={20} />
                Resend Activation Email
              </ActionButton>
              
              <SecondaryButton
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/auth?tab=register')}
              >
                <ArrowLeft size={20} />
                Back to Registration
              </SecondaryButton>
            </div>
          )}
          
          {showResendForm && (
            <ResendForm>
              <ResendFormTitle>Resend Activation Email</ResendFormTitle>
              <form onSubmit={handleResendActivation}>
                <EmailInput
                  type="email"
                  placeholder="Enter your email address"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  required
                />
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <ActionButton
                    type="submit"
                    disabled={resending || !resendEmail.trim()}
                    whileHover={{ scale: resending ? 1 : 1.02 }}
                    whileTap={{ scale: resending ? 1 : 0.98 }}
                  >
                    {resending ? (
                      <>
                        <Loader size={16} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Send Email
                      </>
                    )}
                  </ActionButton>
                  
                  <SecondaryButton
                    type="button"
                    onClick={() => {
                      setShowResendForm(false);
                      setResendEmail('');
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </form>
            </ResendForm>
          )}
        </ContentCard>
      </ActivationContainer>
    </>
  );
};

export default AccountActivation;
